import { noop } from "lodash";
import { PropsWithChildren, useEffect } from "react";
import { Route, RouteProps, Switch, useLocation } from "react-router-dom";
import { Redirect } from "react-router-dom";
import { CreatedArtifactFragmentFragment } from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import Account from "@apps/account/account";
import ArtifactCreationDialog from "@apps/artifact-creation-dialog/artifact-creation-dialog";
import ArtifactFullscreen from "@apps/artifact-fullscreen/artifact-fullscreen";
import ArtifactSidebarRoute from "@apps/artifact-sidebar/artifact-sidebar-route";
import AssessmentSidebarRoute from "@apps/assessments/components/assessment/assessment-sidebar-route";
import AssessmentDeliverySidebarRoute from "@apps/assessments/components/delivery/assessment-delivery-sidebar-route";
import Assessments from "@apps/assessments/page";
import CoachingPage from "@apps/coaching/coaching-page";
import ConnectCalendarModal from "@apps/connect-calendar-modal/connect-calendar-modal";
import DashboardNew from "@apps/dashboard-new/dashboard";
import RedirectDashboardToProfile from "@apps/dashboard-new/redirect-dashboard-to-profile";
import EngagementSurvey from "@apps/engagement-survey/page";
import Error404 from "@apps/error-404";
import ExplorerPage from "@apps/explorer/page";
import FeedbackPage from "@apps/feedback/feedback";
import GoalAlignment from "@apps/goal-alignment/goal-alignment";
import GoalOverview from "@apps/goal-overview/page";
import GoogleCalSetup from "@apps/google-cal-setup";
import Home from "@apps/home/home";
import InviteDialog from "@apps/invite-dialog/invite-dialog";
import KPIs from "@apps/kpis/page";
import MeetingDialog from "@apps/meeting-dialog/meeting-dialog";
import MeetingGroupRoutes from "@apps/meeting-new/meeting-group-routes";
import MeetingRoutes from "@apps/meeting-new/meeting-routes";
import MeetingsRoutes from "@apps/meeting-new/meetings-routes";
import MeetingSettings from "@apps/meeting-settings/settings";
import MeetingSidebar from "@apps/meeting-sidebar/meeting-sidebar";
import OrgChart from "@apps/org-chart/org-chart";
import OrgSettings from "@apps/org-settings/org-settings";
import ProfilePage from "@apps/profile/profile-page";
import ProfileSidebar from "@apps/profile/profile-sidebar";
import Programs from "@apps/programs/page";
import Pusher from "@apps/pusher/pusher";
import Ratings from "@apps/ratings/page";
import RecognitionBoard from "@apps/recognition-board/recognition-board";
import Reporting from "@apps/reporting/page";
import StyleGuide from "@apps/styleguide/styleguide";
import Templates from "@apps/templates/page";
import WorkHistory from "@apps/work-history/page";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import BillingDialog from "@components/billing-dialog-cta/billing-dialog";
import { useLink } from "@components/link/link-helpers";
import { artifactTypeUrl } from "@helpers/constants";
import { getUrl, toWithBackground } from "@helpers/helpers";

import Layout from "./components/layout";
import { artifactTypePaths, meetingsRoutes } from "./routes-constants";

const CustomRouteWithPusher: React.FC<RouteProps> = ({
  children,
  ...props
}) => {
  return (
    <Route {...props}>
      <Layout>
        <Pusher>{children}</Pusher>
      </Layout>
    </Route>
  );
};

const CustomRoute: React.FC<PropsWithChildren<RouteProps>> = ({
  children,
  ...props
}) => {
  return (
    <Route {...props}>
      <Layout>{children}</Layout>
    </Route>
  );
};

const Routes: React.FC = () => {
  const currentUser = currentUserVar();
  const link = useLink();
  const currentOrganization = currentOrganizationVar();
  const location = useLocation<TFLocationState>();
  const background = location.state?.background;

  const handleRedirectHomepage = () => {
    link.redirect("/");
  };

  useEffect(() => {
    if (
      !window.Cypress &&
      !import.meta.env.DEV &&
      !window.TF.hijacked &&
      window.Intercom
    ) {
      window.Intercom("update");
    }
  }, [location]);

  // Implemented for the product tour in Intercom
  // we need to redirect to /connect-calendar when
  // the user clicks anchor tag #connect-calendar
  // This prevents a full page refresh
  useEffect(() => {
    if (location.hash === "#connect-calendar") {
      link.replace(
        toWithBackground({
          pathname: "/connect-calendar",
          location,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const handleCloseArtifactCreationDialog = (
    artifact?: CreatedArtifactFragmentFragment,
  ) => {
    if (artifact) {
      link.redirect(
        toWithBackground({
          pathname: getUrl({
            artifactId: artifact.id,
            artifactType: artifact.artifactType,
          }),
          location,
        }),
      );
    } else {
      link.redirect("/");
    }
  };

  return (
    <>
      {/* Pages */}
      <Switch location={background || location}>
        <CustomRouteWithPusher exact path={["/", "/dashboard"]}>
          {waffle.flag_is_active("home") ? <Home /> : <DashboardNew />}
        </CustomRouteWithPusher>
        {waffle.flag_is_active("home") && (
          <CustomRouteWithPusher
            exact
            path={["/ask-ai/:chatSessionId", "/ask-ai/"]}
          >
            <Home />
          </CustomRouteWithPusher>
        )}
        <CustomRouteWithPusher path="/dashboard/user/:userId">
          <RedirectDashboardToProfile />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/recognition-board">
          <RecognitionBoard />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/feedbacks">
          <FeedbackPage />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/explorer">
          <ExplorerPage />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/templates">
          <Templates />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/assessments">
          <Assessments />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/programs">
          <Programs />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/work-history">
          <WorkHistory />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/reporting">
          <Reporting />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/ratings">
          <Ratings />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/kpis">
          <KPIs />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path={["/goals/:type/:id", "/goals"]}>
          <GoalOverview />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/goal-alignment">
          <GoalAlignment />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/account">
          <Account />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/org-chart">
          <OrgChart />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/coaching">
          <CoachingPage />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/survey">
          <EngagementSurvey />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/google-cal-setup">
          <GoogleCalSetup />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher
          path={[
            "/settings/organization/:organizationId",
            "/settings/organization",
          ]}
        >
          <OrgSettings />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/invite">
          <DashboardNew />
          <InviteDialog organization={currentOrganization} />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/connect-calendar">
          <DashboardNew />
          <ConnectCalendarModal open={true} onClose={handleRedirectHomepage} />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path="/billing">
          <DashboardNew />
          <BillingDialog
            onClose={() => {
              link.redirect("/");
            }}
            organization={currentOrganization}
          />
        </CustomRouteWithPusher>

        <CustomRouteWithPusher path="/new-meeting">
          <MeetingDialog onClose={noop} />
        </CustomRouteWithPusher>

        {/* MEETING GROUP, MEETING & TOPIC */}
        <CustomRouteWithPusher path="/meeting/:meetingGroupId/settings">
          <MeetingSettings />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path={"/meeting/:meetingGroupId/:meetingId"}>
          <MeetingRoutes />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher exact path={"/meeting/:meetingGroupId"}>
          <MeetingGroupRoutes />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path={meetingsRoutes}>
          <MeetingsRoutes />
        </CustomRouteWithPusher>

        <CustomRouteWithPusher path="/styleguide">
          <StyleGuide />
        </CustomRouteWithPusher>

        <CustomRouteWithPusher path="/profile/:profileId">
          <ProfilePage />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher
          path={Object.values(artifactTypeUrl).map((path) => `/${path}-new`)}
        >
          <DashboardNew />
          <ArtifactCreationDialog onClose={handleCloseArtifactCreationDialog} />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher
          exact
          path={`/(${artifactTypePaths})/:artifactId/fullscreen`}
        >
          <ArtifactFullscreen />
        </CustomRouteWithPusher>
        <CustomRouteWithPusher path={`/(${artifactTypePaths})/:artifactId`}>
          <DashboardNew />
          <ArtifactSidebarRoute />
        </CustomRouteWithPusher>

        {/* login/register links */}
        <CustomRouteWithPusher path={["/login", "/register"]}>
          <Redirect to={`/dashboard/user/${currentUser.id}`} />
        </CustomRouteWithPusher>
        {/* Not found */}
        <CustomRouteWithPusher path="*">
          <Error404 />
        </CustomRouteWithPusher>
      </Switch>

      {/* Sidebars */}
      {background && (
        <>
          <CustomRoute
            path={Object.values(artifactTypeUrl).map((path) => `/${path}-new`)}
          >
            <ArtifactCreationDialog
              onClose={handleCloseArtifactCreationDialog}
            />
          </CustomRoute>
          <CustomRoute path="/connect-calendar">
            <ConnectCalendarModal
              open={true}
              onClose={handleRedirectHomepage}
            />
          </CustomRoute>

          <CustomRoute path="/invite">
            <InviteDialog organization={currentOrganization} />
          </CustomRoute>

          <CustomRoute
            exact
            path={[
              `/(${artifactTypePaths})/:artifactId`,
              `/meeting/:meetingGroupId/:meetingId/(${artifactTypePaths})/:artifactId`,
            ]}
          >
            <ArtifactSidebarRoute />
          </CustomRoute>
          <CustomRoute path={["/profile/:profileId"]}>
            <ProfileSidebar />
          </CustomRoute>
          <CustomRoute exact path={["/meeting/:meetingGroupId/:meetingId"]}>
            <MeetingSidebar />
          </CustomRoute>
          <CustomRoute
            exact
            path={"/assessments/assessment/delivery/:deliveryId"}
          >
            <AssessmentDeliverySidebarRoute />
          </CustomRoute>
          <CustomRoute exact path={"/assessments/assessment/:assessmentId"}>
            <AssessmentSidebarRoute />
          </CustomRoute>
        </>
      )}
    </>
  );
};

export default Routes;
